import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageShell, SoftCard, SecondaryButton } from "./ui";
import EntryMoment from "./EntryMoment";
import { useAstriaCopy, CopyKey } from "./copy";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import toast from "react-hot-toast";
import { useTheme } from "../../theme/useTheme";
import { APP_LANGUAGES, isAppLanguage } from "../../utils/languages";
import PlansBilling, { PlansFooter } from "./PlansBilling";
import PlanSummary from "./PlanSummary";


const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between py-3">
    <span className="text-astria-text">{label}</span>
    {children}
  </div>
);

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    aria-pressed={on}
    className="relative h-6 w-11 rounded-full transition"
    style={{ background: on ? "var(--astria-accent-primary)" : "var(--astria-surface-soft)" }}
  >
    <span
      className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
      style={{ left: on ? 22 : 2 }}
    />
  </button>
);

const AccountPage: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, updateCurrentUser } = useAuth();
  const { i18n, t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const c = useAstriaCopy();
  const section = pathname.split("/")[2] || "profile";
  const [entry, setEntry] = useState(false);
  const [glow, setGlow] = useState(true);
  const [stars, setStars] = useState(true);
  const [remember, setRemember] = useState(true);
  const [suggest, setSuggest] = useState(false);

  useEffect(() => {
    const code = (i18n.language || "en").split("-")[0];
    document.documentElement.setAttribute("data-astria-lang", isAppLanguage(code) ? code : "en");
  }, [i18n.language]);

  // The chosen language becomes the user's preferred language everywhere:
  // app state, the saved user, localStorage, the UI language and their account.
  const pickLanguage = async (code: string) => {
    if (!isAppLanguage(code)) return;
    const current = (user?.preferredLanguage || i18n.language || "en").split("-")[0];
    updateCurrentUser({ preferredLanguage: code });
    setEntry(true);
    if (code === current || !user?._id || user.roleId === 3) return; // testers' profiles are locked
    try {
      await api.put(`/backend/user/${user._id}`, { preferredLanguage: code });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Your language was changed here, but could not be saved to your account.",
      );
    }
  };

  const TITLES: Record<string, CopyKey> = {
    profile: "navProfile",
    language: "navLanguage",
    atmosphere: "navAtmosphere",
    "memory-privacy": "navMemory",
    "plans-billing": "navPlans",
  };

  return (
    <>
    <PageShell title={c(TITLES[section] || "navAccount")} wide={section === "plans-billing"}
      flushBottom={section === "plans-billing"}
      onBack={section === "plans-billing" ? () => navigate("/") : undefined}
      backLabel={t("astria.planBack", "Back")}
    >
      <div className="flex flex-wrap justify-center gap-2" role="tablist">
        {(Object.keys(TITLES) as string[]).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={section === id}
            onClick={() => navigate(`/account/${id}`)}
            className="px-4 py-2 text-sm transition"
            style={{
              borderRadius: "var(--astria-radius-pill)",
              background: section === id ? "var(--astria-accent-glow)" : "var(--astria-surface-soft)",
              color: section === id ? "var(--astria-text-primary)" : "var(--astria-text-secondary)",
            }}
          >
            {c(TITLES[id])}
          </button>
        ))}
      </div>

      {entry && (
        <EntryMoment headline={c("languageEntry")} subheadline={c("languageEntrySub")} onDone={() => setEntry(false)} />
      )}

      {section === "profile" && (
        <>
        <SoftCard>
          <Row label={c("accName")}><span className="text-astria-muted">{user?.username || "—"}</span></Row>
          <Row label={c("accEmail")}><span className="text-astria-muted">{user?.email || "—"}</span></Row>
          <Row label={t("astria.accLightTheme", { defaultValue: "Light theme" })}>
            <Toggle on={theme === "light"} onChange={toggleTheme} />
          </Row>
          <Row label={c("accDob")}><span className="text-astria-muted">{user?.dob ? String(user.dob).slice(0, 10) : c("accNotSet")}</span></Row>
        </SoftCard>
        <PlanSummary />
        </>
      )}

      {section === "language" && (
        <SoftCard>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {APP_LANGUAGES.map((l) => {
              const active = (i18n.language || "en").split("-")[0] === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  data-astria-lang={l.code}
                  onClick={() => pickLanguage(l.code)}
                  className="px-4 py-4 text-left text-astria-text transition hover:brightness-125"
                  style={{
                    borderRadius: "var(--astria-radius-card)",
                    background: active
                      ? "linear-gradient(135deg, var(--astria-lang-tint) -80%, var(--astria-surface-soft))"
                      : "var(--astria-surface-soft)",
                    border: active ? "1px solid var(--astria-lang-tint)" : "1px solid transparent",
                  }}
                >
                  {l.nativeName}
                </button>
              );
            })}
          </div>
        </SoftCard>
      )}

      {section === "atmosphere" && (
        <SoftCard>
          <Row label={c("accGlow")}><Toggle on={glow} onChange={setGlow} /></Row>
          <Row label={c("accStars")}><Toggle on={stars} onChange={setStars} /></Row>
        </SoftCard>
      )}

      {section === "memory-privacy" && (
        <SoftCard>
          <Row label={c("accRemember")}><Toggle on={remember} onChange={setRemember} /></Row>
          <Row label={c("accSuggest")}><Toggle on={suggest} onChange={setSuggest} /></Row>
          <p className="mt-3 text-xs text-astria-muted">{c("accPrivacy")}</p>
          <SecondaryButton className="mt-4">{c("accClear")}</SecondaryButton>
        </SoftCard>
      )}

      {section === "plans-billing" && <PlansBilling />}
    </PageShell>
    {section === "plans-billing" && <PlansFooter />}
    </>
  );
};

export default AccountPage;
